import { inject, PLATFORM_ID } from '@angular/core';
import { 
  ActivatedRouteSnapshot, 
  Router, 
  RouterStateSnapshot,
  CanActivateFn,
  RedirectCommand,
  UrlTree
} from '@angular/router';
import { StreamService } from '../services/stream_service';
import { UserService } from '../services/user_service';
import { isPlatformServer } from '@angular/common';

export interface User {
  chOwned: { [key: string]: string | number };
}

export const streamOwnerGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const streamService = inject(StreamService);
  const userService = inject(UserService);
  const platformId = inject(PLATFORM_ID);

  const streamId = route.params['id'];
  const customUrl = route.params['custom-url'];
  const homeUrl: UrlTree = router.parseUrl('/');

  const isOwnerOfStream = (id: string | number, userStreams: any): boolean => {
    const streams = Object.values(userStreams || {}).map(v => v.toString());
    return !!(streams.length > 0 && id && streams.includes(id.toString()));
  };

  const checkOwnership = (user: User): Promise<boolean> => {
    const streamsOwned = user.chOwned || {};

    if (customUrl) {
      return streamService.getStreamIdFromCustomUrl(customUrl)
        .then(result => isOwnerOfStream(result.channel.id, streamsOwned))
        .catch(err => {
          console.error('Error while fetching stream custom-url data', err);
          return false;
        });
    }

    if (!streamId) {
      return Promise.resolve(false);
    }

    return streamService.getStreamInfo(streamId)
      .then(result => isOwnerOfStream(result.id, streamsOwned))
      .catch(err => {
        console.error('Error while fetching stream info', err);
        return false;
      });
  };

  return userService.getProfile().then((user: User) => {
    if (isPlatformServer(platformId) || !user) {
      return new RedirectCommand(homeUrl);
    }

    return checkOwnership(user).then(isOwner => {
      if (isOwner) {
        return true;
      }

      console.log('StreamOwnerGuard: Initial check failed, retrying with fresh profile...');
      return userService.getProfile(true).then(freshUser => {
        if (!freshUser) {
          return new RedirectCommand(homeUrl);
        }

        return checkOwnership(freshUser).then(isOwnerFresh => {
          return isOwnerFresh ? true : new RedirectCommand(homeUrl);
        });
      });
    });
  }, () => {
    return new RedirectCommand(homeUrl);
  });
};